import { jsonStringify } from 'utils/json';

export class TelephonyApi {
  #httpSkynetApi;
  #httpTelephonyApi;

  constructor(httpSkynetApi, httpTelephonyApi) {
    this.#httpSkynetApi = httpSkynetApi;
    this.#httpTelephonyApi = httpTelephonyApi;
  }

  async getTelephonyInternalUserExtensionData({ userId }) {
    return this.#httpSkynetApi.get(`/Telephony/internal/${userId}/UserExtensionData`);
  }

  async getTelephonyUserExtensionData({ userId }) {
    return this.#httpSkynetApi.get(`/Telephony/${userId}/UserExtensionData`);
  }

  async getTelephonyUser() {
    return this.#httpTelephonyApi.get('/api/call/userInfo');
  }

  async updateUserStatus({ contactStatus }) {
    return this.#httpTelephonyApi.post('/api/call/updateSipStatus', null, {
      params: { status: contactStatus },
    });
  }

  async getTelnyxJwt() {
    return this.#httpTelephonyApi.get('/api/telnyx/jwt');
  }

  // DEV-6051: per-session WebRTC credential mint, replacing the shared static SIP password. `kind`
  // is 'web' (per-tab) or 'mobileDevice' (per-install); `sessionId` is that tab's or device's stable id.
  async createTelnyxSession({ sessionId, kind }) {
    return this.#httpTelephonyApi.post('/api/telnyx/session', { sessionId, kind });
  }

  async scheduleCall({ dialedNumber, callerNumber }) {
    return this.#httpTelephonyApi.post('/api/call/schedule', {
      inMins: 0,
      payload: jsonStringify({ dialedNumber, callerNumber }),
    });
  }

  async saveCall({ dispositionType, note, telnyxCallId }) {
    return this.#httpTelephonyApi.post('/api/call/save', {
      dispositionType,
      note,
      telnyxCallId,
    });
  }

  // DEV-4332 (native cold-wake): tells the backend this freshly-registered device is now ready to receive
  // the inbound call, so it dials an additional ring leg to this credential. callId = the inbound call_id
  // from the VoIP push. No body; the user JWT is attached by the shared telephony API client interceptor.
  async registerInboundReady({ callId }) {
    return this.#httpTelephonyApi.post(`/api/call/inbound/${callId}/register-ready`);
  }

  async getContacts() {
    return this.#httpTelephonyApi.get('/api/call/contacts');
  }

  async getCallHistory() {
    return this.#httpTelephonyApi.get('/api/call/history');
  }

  async acceptLead({ notificationId }) {
    return this.#httpTelephonyApi.post('/api/call/accept', null, {
      params: { notificationId },
    });
  }

  async rejectLead({ notificationId, reason }) {
    return this.#httpTelephonyApi.post('/api/call/reject', null, {
      params: { notificationId, reason },
    });
  }

  async timeoutLead({ notificationId }) {
    return this.#httpTelephonyApi.post('/api/call/timeout', null, {
      params: { notificationId },
    });
  }

  // Text messaging (DEV-1453)
  async getTextLeads({ skip, take, phoneNumber } = {}) {
    return this.#httpTelephonyApi.get('/api/chat/leads', {
      params: { skip, take, phoneNumber },
    });
  }

  async getTextMessages({ phoneNumberTo }) {
    return this.#httpTelephonyApi.get('/api/chat/messages', {
      params: { phoneNumberTo },
    });
  }

  async sendTextMessage({ toPhoneNumber, text }) {
    return this.#httpTelephonyApi.post('/api/chat/send', { toPhoneNumber, text });
  }

  // Web chats / Borrower chat (DEV-1454)
  async getWebChats() {
    return this.#httpTelephonyApi.get('/api/webChats');
  }

  async sendWebChatMessage({ chatId, message }) {
    return this.#httpTelephonyApi.post(`/api/webChats/${chatId}/messages`, { message });
  }

  // Voicemail inbox (DEV-1577)
  async getVoicemails({ page, pageSize } = {}) {
    return this.#httpTelephonyApi.get('/api/Voicemail', {
      params: { page, pageSize },
    });
  }

  async getVoicemailAudioUrl({ voicemailId }) {
    return this.#httpTelephonyApi.get(`/api/Voicemail/${voicemailId}/url`);
  }

  async markVoicemailRead({ voicemailId }) {
    return this.#httpTelephonyApi.put(`/api/Voicemail/${voicemailId}/read`);
  }

  async markAllVoicemailsRead() {
    return this.#httpTelephonyApi.put('/api/Voicemail/read-all');
  }

  async deleteVoicemail({ voicemailId }) {
    return this.#httpTelephonyApi.delete(`/api/Voicemail/${voicemailId}`);
  }

  async getVoicemailUnreadCount() {
    return this.#httpTelephonyApi.get('/api/Voicemail/unread-count');
  }
}
